import type { JudgeRegistration, JudgeScoreStatus } from '../types/judging';

export type JudgeStatusFilter = 'all' | JudgeScoreStatus;
export type JudgeSortMode = 'class' | 'name' | 'status';

export interface JudgeListFilters {
  classId: number | null; // null = all classes
  status: JudgeStatusFilter;
  search: string;
  sort: JudgeSortMode;
}

const STATUS_ORDER: Record<JudgeScoreStatus, number> = {
  not_started: 0,
  draft: 1,
  finalized: 2,
};

export function vehicleLabel(r: JudgeRegistration): string {
  return r.vehicle.nickname || `${r.vehicle.year} ${r.vehicle.make} ${r.vehicle.model}`.trim();
}

export function matchesJudgeFilters(
  r: JudgeRegistration,
  filters: Pick<JudgeListFilters, 'classId' | 'status' | 'search'>
): boolean {
  const scoreStatus = r.my_score_status || 'not_started';
  if (filters.classId !== null && r.class_id !== filters.classId) return false;
  if (filters.status !== 'all' && scoreStatus !== filters.status) return false;
  const q = filters.search.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    vehicleLabel(r),
    r.class_name || '',
    r.owner_username,
    r.owner_display_name || '',
    r.vehicle.make,
    r.vehicle.model,
    r.vehicle.trim || '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function sortJudgeRegistrations(
  regs: JudgeRegistration[],
  sort: JudgeSortMode
): JudgeRegistration[] {
  const copy = [...regs];
  copy.sort((a, b) => {
    if (sort === 'name') {
      return vehicleLabel(a).localeCompare(vehicleLabel(b), undefined, { sensitivity: 'base' });
    }
    if (sort === 'status') {
      const aStatus = a.my_score_status || 'not_started';
      const bStatus = b.my_score_status || 'not_started';
      const diff = STATUS_ORDER[aStatus] - STATUS_ORDER[bStatus];
      if (diff !== 0) return diff;
      return vehicleLabel(a).localeCompare(vehicleLabel(b), undefined, { sensitivity: 'base' });
    }
    // class (default): preserve class_id grouping, then name
    const aClass = a.class_id ?? Number.MAX_SAFE_INTEGER;
    const bClass = b.class_id ?? Number.MAX_SAFE_INTEGER;
    if (aClass !== bClass) return aClass - bClass;
    const nameCmp = (a.class_name || '').localeCompare(b.class_name || '', undefined, {
      sensitivity: 'base',
    });
    if (nameCmp !== 0) return nameCmp;
    return vehicleLabel(a).localeCompare(vehicleLabel(b), undefined, { sensitivity: 'base' });
  });
  return copy;
}

export function filterAndSortRegistrations(
  registrations: JudgeRegistration[],
  filters: JudgeListFilters
): JudgeRegistration[] {
  const filtered = registrations.filter((r) => matchesJudgeFilters(r, filters));
  return sortJudgeRegistrations(filtered, filters.sort);
}

/** First unfinished registration in the filtered/sorted list (draft or not_started). */
export function findFirstUnfinished(regs: JudgeRegistration[]): JudgeRegistration | null {
  return regs.find((r) => (r.my_score_status || 'not_started') !== 'finalized') ?? null;
}

export function findNextInQueue(
  regs: JudgeRegistration[],
  currentId: number
): JudgeRegistration | null {
  const idx = regs.findIndex((r) => r.id === currentId);
  if (idx < 0 || idx >= regs.length - 1) return null;
  return regs[idx + 1] ?? null;
}

export function findPrevInQueue(
  regs: JudgeRegistration[],
  currentId: number
): JudgeRegistration | null {
  const idx = regs.findIndex((r) => r.id === currentId);
  if (idx <= 0) return null;
  return regs[idx - 1] ?? null;
}

export function findNextUnscored(
  regs: JudgeRegistration[],
  currentId: number
): JudgeRegistration | null {
  const idx = regs.findIndex((r) => r.id === currentId);
  const start = idx < 0 ? 0 : idx + 1;
  for (let i = start; i < regs.length; i++) {
    if ((regs[i].my_score_status || 'not_started') !== 'finalized') return regs[i];
  }
  for (let i = 0; i < start; i++) {
    if (regs[i].id !== currentId && (regs[i].my_score_status || 'not_started') !== 'finalized') {
      return regs[i];
    }
  }
  return null;
}

export function allFinalizedInClass(
  registrations: JudgeRegistration[],
  classId: number | null
): boolean {
  const inClass = registrations.filter((r) =>
    classId === null ? true : r.class_id === classId
  );
  return inClass.length > 0 && inClass.every((r) => (r.my_score_status || 'not_started') === 'finalized');
}

export const JUDGE_QUEUE_STORAGE_KEY = (eventId: number) => `judge_queue_${eventId}`;

export function storeJudgeQueue(eventId: number, registrationIds: number[]): void {
  try {
    sessionStorage.setItem(JUDGE_QUEUE_STORAGE_KEY(eventId), JSON.stringify(registrationIds));
  } catch {
    // ignore quota / private mode
  }
}

export function loadJudgeQueue(eventId: number): number[] | null {
  try {
    const raw = sessionStorage.getItem(JUDGE_QUEUE_STORAGE_KEY(eventId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(Number).filter((n) => n > 0) : null;
  } catch {
    return null;
  }
}

export function buildScorecardReturnQuery(filters: {
  classId: number | null;
  status: JudgeStatusFilter;
}): string {
  const params = new URLSearchParams();
  if (filters.classId !== null) params.set('class', String(filters.classId));
  if (filters.status !== 'all') params.set('status', filters.status);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function parseJudgeListQuery(search: string): {
  classId: number | null;
  status: JudgeStatusFilter;
} {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const classRaw = params.get('class');
  const statusRaw = params.get('status');
  const classId =
    classRaw && !Number.isNaN(Number(classRaw)) && Number(classRaw) > 0
      ? Number(classRaw)
      : null;
  const validStatuses: JudgeScoreStatus[] = ['not_started', 'draft', 'finalized'];
  const status: JudgeStatusFilter =
    statusRaw && (validStatuses as string[]).includes(statusRaw)
      ? (statusRaw as JudgeScoreStatus)
      : 'all';
  return { classId, status };
}
